import { BlockContentProps } from '@sanity/block-content-to-react'

import { useSanityContextBasedContent, useSanityRoleBasedContent } from '../../sanity'
import { SanityContext } from '../../sanity/context-based-content'
import { SanityUserRole } from '../../sanity/roles'

/*
    Example of contextualDescription:
    [{
      _key: e64ee27d81bf,
      _type: contextualDescription,
      contexts: { _type: contextBasedContent, enabled: false },
      roles: { _type: roleBasedContent, enabled: true, roles: [CAT_PATIENT, CAT_PRACTITIONER] },
      description: [BlockContent]
    }, 
    {
      _key: e27bb7638e51,
      _type: contextualDescription,
      contexts: { _type: contextBasedContent, enabled: true, contexts: [WEBSTORE, DISPENSARY]},
      roles: { _type: roleBasedContent, enabled: true, roles: [CAT_EMPLOYEE] },
      description: [BlockContent]
    }]
  */

type ContextualDescription = {
  _key: string
  _type: string
  contexts: {
    _type: string
    enabled: boolean
    contexts: SanityContext[]
  }
  roles: {
    _type: string
    enabled: boolean
    roles: string[]
  }
  description: BlockContentProps['blocks']
}

const checkAuthenticatedRole = (
  roles: ContextualDescription['roles'],
  userRoles?: readonly SanityUserRole[],
) => {
  if (!userRoles || userRoles.includes(SanityUserRole['Anonymous'])) {
    return !!roles.roles?.includes('ANONYMOUS')
  }
  return userRoles.some((role) => !!roles.roles?.includes(role))
}

/**
 * Get the first contextual description that matches the current context and user roles
 * @param currentContext - The current context (dispensary or webstore)
 * @param userRoles - The roles the current user has
 * @returns boolean
 */
const findByRolesAndContexts =
  (currentContext: SanityContext | null, userRoles?: readonly SanityUserRole[]) =>
  ({ roles, contexts }: ContextualDescription): boolean => {
    if (currentContext) {
      if (!roles.enabled && !contexts.enabled) {
        return false
      } else if (!roles.enabled && contexts.enabled) {
        return !!contexts.contexts?.includes(currentContext)
      } else if (roles.enabled && !contexts.enabled) {
        return checkAuthenticatedRole(roles, userRoles)
      } else {
        return (
          checkAuthenticatedRole(roles, userRoles) && !!contexts.contexts?.includes(currentContext)
        )
      }
    } else {
      return false
    }
  }

export interface UseProductDescriptionParams {
  contextualDescription?: ContextualDescription[]
  parentContextualDescription?: ContextualDescription[]
  defaultDescription?: BlockContentProps['blocks']
  parentDefaultDescription?: BlockContentProps['blocks']
}

function useProductDescription({
  contextualDescription,
  parentContextualDescription,
  defaultDescription,
  parentDefaultDescription,
}: UseProductDescriptionParams): unknown | unknown[] | null {
  const { currentContext } = useSanityContextBasedContent()
  const { userRoles } = useSanityRoleBasedContent()

  const contextualChildDescriptions: BlockContentProps['blocks'][] | undefined = (
    contextualDescription ?? []
  )
    ?.filter(findByRolesAndContexts(currentContext, userRoles))
    .map((desc: ContextualDescription) => desc.description)

  const contextualParentDescriptions: BlockContentProps['blocks'][] | undefined = (
    parentContextualDescription ?? []
  )
    ?.filter(findByRolesAndContexts(currentContext, userRoles))
    .map((desc: ContextualDescription) => desc.description)

  if (contextualChildDescriptions?.length) {
    return contextualChildDescriptions
  }

  if (defaultDescription) {
    return defaultDescription
  }

  if (contextualParentDescriptions?.length) {
    return contextualParentDescriptions
  }

  if (parentDefaultDescription) {
    return parentDefaultDescription
  }

  return null
}

export { useProductDescription }
